import { targetingLabel } from '../i18n';

/**
 * The audience a research was ASKED for, put into the same words the editor
 * uses to ask for it. The dashboard shows this next to the demographics that
 * actually answered, so a skew in the sample can be read against the brief.
 *
 * Section headings are catalogue keys; the values are the same English
 * vocabulary the rest of the targeting UI stores, named for display only. A
 * code we don't know is printed rather than hidden.
 */

export interface ResearchTargeting {
  geo?: Array<{ id?: string; name?: string; type?: string; level?: string; country?: string; canonical_name?: string }>;
  demographics?: {
    age_ranges?: string[];
    genders?: string[];
    household_income?: string[];
    parental_statuses?: string[];
  };
  audiences?: Array<{ id?: string; name?: string; display_name?: string } | string>;
}

export interface TargetingSection {
  labelKey: string;
  values: string[];
}

const AGE: Record<string, string> = {
  AGE_RANGE_18_24: '18–24',
  AGE_RANGE_25_34: '25–34',
  AGE_RANGE_35_44: '35–44',
  AGE_RANGE_45_54: '45–54',
  AGE_RANGE_55_64: '55–64',
  AGE_RANGE_65_PLUS: '65+',
};

const GENDER: Record<string, string> = {
  GENDER_MALE: 'Male',
  GENDER_FEMALE: 'Female',
  GENDER_UNKNOWN: 'Unknown',
};

const PARENTAL: Record<string, string> = {
  PARENTAL_STATUS_PARENT: 'Parent',
  PARENTAL_STATUS_NOT_A_PARENT: 'Not a Parent',
  PARENTAL_STATUS_UNKNOWN: 'Unknown',
};

const INCOME: Record<string, string> = {
  top10: 'Very High',
  top20: 'High',
  top30: 'Above Average',
  top40: 'Average',
  top50: 'Below Average',
  lower50: 'Low',
};

/** An unmapped code still has to read as something — strip the prefix, not the value. */
const humanise = (code: string): string =>
  code
    .replace(/^(AGE_RANGE|GENDER|PARENTAL_STATUS|HOUSEHOLD_INCOME)_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

const map = (codes: string[] | undefined, table: Record<string, string>): string[] =>
  (codes || []).map((c) => targetingLabel(table[c] || humanise(c)));

/** Geo entries carry a full canonical name; the short one reads better in a chip. */
const geoLabel = (g: NonNullable<ResearchTargeting['geo']>[number]): string =>
  (g.name || g.canonical_name || String(g.id || '')).split(',')[0].trim();

export function describeTargeting(t?: ResearchTargeting | null): TargetingSection[] {
  if (!t) return [];
  const d = t.demographics || {};
  const sections: TargetingSection[] = [
    { labelKey: 'research.audGeography', values: (t.geo || []).map(geoLabel).filter(Boolean) },
    { labelKey: 'research.audAge', values: map(d.age_ranges, AGE) },
    { labelKey: 'research.audGender', values: map(d.genders, GENDER) },
    { labelKey: 'research.audIncome', values: map(d.household_income, INCOME) },
    { labelKey: 'research.audParental', values: map(d.parental_statuses, PARENTAL) },
    {
      labelKey: 'research.audAudiences',
      values: (t.audiences || []).map((a) =>
        typeof a === 'string' ? a : a.display_name || a.name || String(a.id || '')
      ).filter(Boolean),
    },
  ];
  // A section nobody filled in says nothing — an empty age list means "all ages",
  // and printing "Age: —" for it would read like a setting that was lost.
  return sections.filter((s) => s.values.length > 0);
}
